import type { AppError } from './contract';
import { createAppError, normalizeApiError, transportError } from './errors';
import { toSearchParams } from './query';

export interface ApiClient {
  get<T>(path: string, options?: RequestOptions): Promise<ApiResult<T>>;
  post<T>(path: string, body: unknown, options?: RequestOptions): Promise<ApiResult<T>>;
  patch<T>(path: string, body: unknown, options?: RequestOptions): Promise<ApiResult<T>>;
  download(path: string, options?: RequestOptions): Promise<ApiDownload>;
}

export interface ApiResult<T> {
  readonly data: T;
  readonly requestId: string | null;
}

export interface ApiDownload {
  readonly blob: Blob;
  readonly filename: string | null;
  readonly requestId: string | null;
}

export interface RequestOptions {
  readonly query?: Readonly<Record<string, unknown>>;
  readonly signal?: AbortSignal;
  readonly idempotencyKey?: string;
  readonly accessToken?: string;
}

export interface CreateApiClientOptions {
  readonly baseUrl: string;
  readonly fetcher?: typeof fetch;
  readonly getAccessToken?: () => string | null | undefined | Promise<string | null | undefined>;
  readonly createRequestId?: () => string | null | undefined;
}

export function createApiClient({
  baseUrl,
  fetcher = fetch,
  getAccessToken,
  createRequestId,
}: CreateApiClientOptions): ApiClient {
  return {
    get: <T>(path: string, options?: RequestOptions) =>
      requestJson<T>('GET', path, undefined, options),
    post: <T>(path: string, body: unknown, options?: RequestOptions) =>
      requestJson<T>('POST', path, body, options),
    patch: <T>(path: string, body: unknown, options?: RequestOptions) =>
      requestJson<T>('PATCH', path, body, options),
    download: (path: string, options?: RequestOptions) => requestDownload(path, options),
  };

  async function requestJson<T>(
    method: 'GET' | 'POST' | 'PATCH',
    path: string,
    body: unknown,
    options: RequestOptions | undefined,
  ): Promise<ApiResult<T>> {
    const response = await request(method, path, body, options);
    if (!response.ok) throw await normalizeApiError(response);

    try {
      return { data: (await response.json()) as T, requestId: requestIdFrom(response) };
    } catch {
      throw invalidResponseError(response);
    }
  }

  async function requestDownload(
    path: string,
    options: RequestOptions | undefined,
  ): Promise<ApiDownload> {
    const response = await request('GET', path, undefined, options);
    if (!response.ok) throw await normalizeApiError(response);
    if (!isCsv(response)) throw unexpectedContentTypeError(response);

    let blob: Blob;
    try {
      blob = await response.blob();
    } catch {
      throw invalidDownloadResponseError(response);
    }

    return {
      blob,
      filename: safeFilename(response.headers.get('content-disposition')),
      requestId: requestIdFrom(response),
    };
  }

  async function request(
    method: 'GET' | 'POST' | 'PATCH',
    path: string,
    body: unknown,
    options: RequestOptions | undefined,
  ): Promise<Response> {
    try {
      const headers = new Headers();
      const accessToken = options?.accessToken ?? (await getAccessToken?.());
      const requestId = createRequestId?.();
      if (accessToken !== undefined && accessToken !== null && accessToken.length > 0) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      if (requestId !== undefined && requestId !== null && requestId.length > 0) {
        headers.set('X-Request-Id', requestId);
      }
      if (method !== 'GET') headers.set('Content-Type', 'application/json');
      if (options?.idempotencyKey !== undefined && options.idempotencyKey.length > 0) {
        headers.set('Idempotency-Key', options.idempotencyKey);
      }

      const requestInit: RequestInit = {
        method,
        headers,
        ...(method === 'GET' ? {} : { body: JSON.stringify(body) }),
      };
      if (options?.signal !== undefined) requestInit.signal = options.signal;

      return await fetcher(toUrl(baseUrl, path, options?.query), requestInit);
    } catch {
      throw transportError();
    }
  }
}

function toUrl(
  baseUrl: string,
  path: string,
  query: Readonly<Record<string, unknown>> | undefined,
): string {
  const base = baseUrl.replace(/\/+$/u, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  const params = query === undefined ? '' : toSearchParams(query).toString();
  return params.length === 0 ? `${base}${suffix}` : `${base}${suffix}?${params}`;
}

function requestIdFrom(response: Response): string | null {
  const requestId = response.headers.get('x-request-id')?.trim();
  return requestId === undefined || requestId.length === 0 ? null : requestId;
}

function isCsv(response: Response): boolean {
  return (
    response.headers.get('content-type')?.split(';', 1)[0]?.trim().toLowerCase() === 'text/csv'
  );
}

function unexpectedContentTypeError(response: Response): AppError {
  return createAppError({
    status: response.status,
    code: 'UNEXPECTED_CONTENT_TYPE',
    message: 'The API did not return a CSV download',
    requestId: requestIdFrom(response),
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}

function invalidResponseError(response: Response): AppError {
  return createAppError({
    status: response.status,
    code: 'INVALID_RESPONSE',
    message: 'The API returned an invalid JSON response',
    requestId: requestIdFrom(response),
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}

function invalidDownloadResponseError(response: Response): AppError {
  return createAppError({
    status: response.status,
    code: 'INVALID_RESPONSE',
    message: 'The API returned an invalid CSV response',
    requestId: requestIdFrom(response),
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}

function safeFilename(contentDisposition: string | null): string | null {
  if (contentDisposition === null) return null;

  const match = /(?:^|;)\s*filename=(?:"([^"]*)"|([^;\s]*))/iu.exec(contentDisposition);
  const filename = match?.[1] ?? match?.[2];
  if (filename === undefined) return null;

  const basename =
    filename
      .split(/[\\/]/u)
      .at(-1)
      ?.replace(/[^A-Za-z0-9._-]/gu, '_') ?? '';
  return basename.length === 0 || basename === '.' || basename === '..' ? null : basename;
}
